import { Link } from 'react-router-dom';
import { 
    Phone, 
    Mail, 
    MapPin, 
    Facebook, 
    Instagram, 
    Send, 
    Truck, 
    Banknote, 
    Shield, 
    Clock, 
    Star,
    ChevronRight,
    Heart
} from 'lucide-react';

export function Footer() {
    const currentYear = new Date().getFullYear();

    return (
        <footer className="hidden md:block bg-gradient-to-b from-gray-900 to-gray-950 text-white pb-0 md:pb-0">
            {/* Trust Bar */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-500">
                <div className="container-custom py-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <TrustBadge icon={Truck} title="Livraison Rapide" desc="58 wilayas" />
                        <TrustBadge icon={Banknote} title="Paiement COD" desc="À la livraison" />
                        <TrustBadge icon={Shield} title="100% Sécurisé" desc="Achat garanti" />
                        <TrustBadge icon={Clock} title="Support 7j/7" desc="Toujours là" />
                    </div>
                </div>
            </div>

            {/* Main Footer */}
            <div className="container-custom py-12 md:py-16">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-12">
                    {/* Brand Section */}
                    <div className="lg:col-span-4">
                        <Link to="/" className="inline-block mb-5">
                            <img
                                src="/logo.png"
                                alt="Marchego"
                                className="h-12 w-auto brightness-0 invert"
                            />
                        </Link>
                        <p className="text-gray-400 text-sm leading-relaxed mb-6">
                            Votre destination shopping N°1 en Algérie. 
                            Produits de qualité, livraison rapide et paiement à la livraison dans les 58 wilayas.
                        </p>
                        
                        {/* Rating */}
                        <div className="inline-flex items-center gap-2 mb-6 bg-gray-800/50 rounded-xl p-3">
                            <div className="flex items-center gap-0.5">
                                {[...Array(5)].map((_, i) => (
                                    <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                                ))}
                            </div>
                            <span className="text-sm font-semibold text-white">4.8/5</span>
                            <span className="text-xs text-gray-400">(2000+ avis)</span>
                        </div>

                        {/* Social Links */}
                        <div className="flex items-center gap-3">
                            <SocialLink href="https://facebook.com" icon={<Facebook className="w-5 h-5" />} label="Facebook" color="hover:bg-blue-600" />
                            <SocialLink href="https://instagram.com" icon={<Instagram className="w-5 h-5" />} label="Instagram" color="hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-500" />
                            <SocialLink href="https://t.me" icon={<Send className="w-5 h-5" />} label="Telegram" color="hover:bg-sky-500" />
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div className="lg:col-span-2">
                        <h4 className="font-bold text-white text-sm uppercase tracking-wider mb-5">Navigation</h4>
                        <ul className="space-y-3">
                            <FooterLink to="/">Accueil</FooterLink>
                            <FooterLink to="/?category=all">Tous les Produits</FooterLink>
                            <FooterLink to="/checkout">Commander</FooterLink>
                        </ul>
                    </div>

                    {/* Customer Service */}
                    <div className="lg:col-span-2">
                        <h4 className="font-bold text-white text-sm uppercase tracking-wider mb-5">Aide</h4>
                        <ul className="space-y-3">
                            <FooterLink to="/faq">Questions Fréquentes</FooterLink>
                            <FooterLink to="/shipping">Livraison & Délais</FooterLink>
                            <FooterLink to="/returns">Retours & Échanges</FooterLink>
                            <FooterLink to="/terms">Conditions d'Utilisation</FooterLink>
                        </ul>
                    </div>

                    {/* Contact Info */}
                    <div className="lg:col-span-4">
                        <h4 className="font-bold text-white text-sm uppercase tracking-wider mb-5">Contact</h4>
                        <ul className="space-y-4">
                            <li>
                                <a href="tel:+213550000000" className="flex items-center gap-4 p-3 bg-gray-800/50 rounded-xl hover:bg-gray-800 transition-colors group">
                                    <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                                        <Phone className="w-5 h-5 text-green-400" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-400">Appelez-nous</p>
                                        <p className="text-white font-semibold group-hover:text-primary-400 transition-colors">0550 00 00 00</p>
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-gray-500 ml-auto" />
                                </a>
                            </li>
                            <li>
                                <a href="mailto:contact@marchego.com" className="flex items-center gap-4 p-3 bg-gray-800/50 rounded-xl hover:bg-gray-800 transition-colors group">
                                    <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                                        <Mail className="w-5 h-5 text-blue-400" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-400">Email</p>
                                        <p className="text-white font-semibold group-hover:text-primary-400 transition-colors">contact@marchego.com</p>
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-gray-500 ml-auto" />
                                </a>
                            </li>
                            <li className="flex items-center gap-4 p-3 bg-gray-800/50 rounded-xl">
                                <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                                    <MapPin className="w-5 h-5 text-orange-400" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-400">Adresse</p>
                                    <p className="text-white font-semibold">Alger, Algérie</p>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Bottom Bar */}
            <div className="border-t border-gray-800">
                <div className="container-custom py-6">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                            © {currentYear} Marchego. Fait avec <Heart className="w-4 h-4 text-red-500 fill-red-500" /> en Algérie
                        </p>
                        <div className="flex items-center gap-6 text-sm text-gray-500">
                            <Link to="/privacy" className="hover:text-white transition-colors">Confidentialité</Link>
                            <Link to="/terms" className="hover:text-white transition-colors">CGV</Link>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
}

// Trust badge component
function TrustBadge({ icon: Icon, title, desc }: { icon: React.ElementType; title: string; desc: string }) {
    return (
        <div className="flex items-center gap-3 text-white">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Icon className="w-5 h-5" />
            </div>
            <div>
                <p className="font-semibold text-sm">{title}</p>
                <p className="text-xs text-white/70">{desc}</p>
            </div>
        </div>
    );
}

// Footer link component
function FooterLink({ to, children }: { to: string; children: React.ReactNode }) {
    return (
        <li>
            <Link
                to={to}
                className="text-gray-400 hover:text-primary-500 transition-colors text-sm"
            >
                {children}
            </Link>
        </li>
    );
}

// Social link component
function SocialLink({
    href,
    icon,
    label,
    color = 'hover:bg-primary-500',
}: {
    href: string;
    icon: React.ReactNode;
    label: string;
    color?: string;
}) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            className={`w-11 h-11 bg-gray-800 rounded-xl flex items-center justify-center text-gray-400 hover:text-white transition-all ${color}`}
        >
            {icon}
        </a>
    );
}
